import { useEffect, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { useParams } from 'react-router-dom';
import GoBack from 'components/actions/GoBack';
import {
    MdDisplaySettings,
    MdDoneOutline,
    MdOutlinePayment,
    MdOutlineRocketLaunch,
    MdInbox,
} from 'react-icons/md';
import { TbTrash } from 'react-icons/tb';
import { getImage } from 'variables/images';
import OrderStatus from 'views/admin/main/ecommerce/orderDetails/components/OrderStatus';
import { toast } from 'react-toastify';
import PayWithWalletButton from 'components/wallet/PayWithWalletButton';

type Palette = 'yellow' | 'green' | 'neutral';

const paletteStyles: Record<Palette, { wrap: string; icon: string; text: string }> = {
    yellow:  { wrap: 'bg-leadrs-yellow-soft', icon: 'text-leadrs-yellow-focus', text: 'text-leadrs-yellow-focus' },
    green:   { wrap: 'bg-leadrs-green-soft',  icon: 'text-leadrs-green-focus',  text: 'text-leadrs-green-focus'  },
    neutral: { wrap: 'bg-white border border-leadrs-stroke', icon: 'text-leadrs-text-soft', text: 'text-leadrs-text-soft' },
};

const InfoBox = (props: { icon?: any; title?: string; description?: string; palette?: Palette }) => {
    const { icon, title, description, palette = 'neutral' } = props;
    const s = paletteStyles[palette];
    return (
        <div className={`rounded-xl p-4 ${s.wrap}`}>
            <div className="mb-2 flex items-center gap-2">
                <span className={s.icon}>{icon}</span>
                <p className={`font-geist text-sm font-semibold ${s.text}`}>{title}</p>
            </div>
            <p className={`font-geist text-sm whitespace-pre-line ${s.text} opacity-80`}>{description}</p>
        </div>
    );
};

const BuyerOfferPage = () => {
    const [user, setUser] = useState<any>(null);
    const [offer, setOffer] = useState<any>({});
    const [imgError, setImgError] = useState(false);
    const params = useParams();

    const initUser = () => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/user/me`, config)
            .then((res) => {
                setUser(res.data);
                if (res.data && res.data.type !== 'buyer') {
                    window.location.href = '/my-request';
                }
            })
            .catch((err) => console.log('No user info found', err));
    };

    const initSheetUrl = () => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/offer/buyer-sheet/${params.id}`, config)
            .catch((err) => console.log('url error', err));
    };

    const getOffer = (id: string) => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/offer/${id}`, config)
            .then((res) => setOffer(res.data))
            .catch((err) => console.log('No offer found', err));
    };

    const deleteOffer = (id: string) => {
        if (!window.confirm('Supprimer cette commande ?')) return;
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .delete(`${CONFIG.apiUrl}/seller-offer/${id}`, config)
            .then(() => {
                toast.success('Votre offre a ete supprimee avec succes !');
                window.location.href = '/';
            })
            .catch((err) => console.log('Delete error', err));
    };

    useEffect(() => {
        if (!localStorage.getItem('token') || localStorage.getItem('token').length < 10)
            window.location.href = '/auth/sign-up';
        getOffer(params.id);
        initUser();
        initSheetUrl();
        // eslint-disable-next-line
    }, []);

    const image = getImage(offer?.industry, offer?.subIndustry);
    const showPlaceholder = !image || imgError;

    return (
        <div>
            {/* Top bar: back + delete */}
            <div className="flex items-center justify-between">
                <GoBack />
                {user?._id === offer?.buyerId && (
                    <button
                        className="flex items-center gap-1.5 font-geist text-sm text-leadrs-text-soft transition-colors hover:text-leadrs-wrong"
                        onClick={() => deleteOffer(offer._id)}
                    >
                        <TbTrash size={16} />
                        <span className="hidden md:inline">Supprimer</span>
                    </button>
                )}
            </div>

            <div className="mt-4 flex flex-col gap-6 lg:flex-row">
                {/* Left column — image + order stepper */}
                <div className="flex flex-col gap-4 lg:w-[380px] lg:flex-shrink-0">
                    {showPlaceholder ? (
                        <div className="flex h-[260px] w-full flex-col items-start justify-end rounded-xl bg-leadrs-purple-soft px-5 pb-5">
                            <p className="font-geist text-[10px] font-semibold uppercase tracking-widest text-leadrs-purple-focus opacity-60">
                                {offer?.subIndustry}
                            </p>
                            <p className="font-crimson text-2xl font-semibold leading-tight text-leadrs-purple-focus">
                                {offer?.industry}
                            </p>
                        </div>
                    ) : (
                        <img
                            src={image}
                            className="w-full rounded-xl object-cover"
                            style={{ maxHeight: 260 }}
                            alt={`${offer?.industry} ${offer?.subIndustry}`}
                            onError={() => setImgError(true)}
                        />
                    )}

                    <OrderStatus offer={offer} />
                </div>

                {/* Right column — details card */}
                <div className="flex-1">
                    <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                        {/* Title block */}
                        <p className="font-geist text-sm text-leadrs-text-soft">{offer?.subIndustry}</p>
                        <h1 className="font-crimson text-4xl font-semibold leading-tight text-leadrs-text">
                            {offer?.industry}
                        </h1>
                        <p className="mt-1 font-geist text-xs text-leadrs-text-soft">
                            Commande #{offer?.offerBuyerNumber}
                        </p>

                        <hr className="my-5 border-leadrs-stroke" />

                        {/* Status blocks */}
                        <div className="flex flex-col gap-3">
                            {offer?.status === 'done' && (
                                <InfoBox
                                    icon={<MdDoneOutline size={18} />}
                                    title="Mission terminee"
                                    description={`Vous avez genere ${offer?.leadsGenerated} leads pour un total de ${(offer?.leadsGenerated * offer?.buyerPrice?.toFixed(2))?.toFixed(2)}€.`}
                                    palette="neutral"
                                />
                            )}
                            {offer?.status === 'on_market' && user?.isValidated && (
                                <InfoBox
                                    icon={<MdDisplaySettings size={18} />}
                                    title="Parametrage de la campagne"
                                    description="Votre campagne est en cours de parametrage. Vous recevrez un mail avec un lien de paiement une fois que tout sera pret !"
                                    palette="yellow"
                                />
                            )}
                            {offer?.status === 'on_market' && !user?.isValidated && (
                                <InfoBox
                                    icon={<MdDisplaySettings size={18} />}
                                    title="En attente de validation"
                                    description="Votre compte doit etre valide par un administrateur avant la publication."
                                    palette="yellow"
                                />
                            )}
                            {offer?.status === 'waiting_payment' && (
                                <>
                                    <InfoBox
                                        icon={<MdOutlinePayment size={18} />}
                                        title="En attente de paiement"
                                        description={`Vous pouvez commander ${offer?.weeklyQuantity} leads a ${offer?.buyerPrice?.toFixed(2)}€ l'unite. Votre campagne commence une fois la commande payee.\nVotre argent reste sous sequestre jusqu'a la livraison complete.`}
                                        palette="yellow"
                                    />
                                    <button
                                        className="w-full rounded-lg bg-leadrs-brand px-5 py-3 font-geist text-sm font-medium text-white transition-opacity hover:opacity-80"
                                        onClick={async () => {
                                            const { data } = await axios.post(
                                                `${CONFIG.apiUrl}/stripe/checkout/${offer._id}`,
                                                {},
                                                { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } }
                                            );
                                            window.location.href = data.url;
                                        }}
                                    >
                                        Payer par carte {(offer?.buyerPrice?.toFixed(2) * offer?.weeklyQuantity)?.toFixed(2)}€
                                    </button>
                                    <PayWithWalletButton
                                        amount={offer?.buyerPrice * offer?.weeklyQuantity}
                                        payUrl={`${CONFIG.apiUrl}/offer/${offer._id}/pay-with-wallet`}
                                        successRedirect="/admin/paiement-success"
                                    />
                                </>
                            )}
                            {offer?.status === 'running' && (
                                <>
                                    <p className="font-geist text-sm text-leadrs-text-soft">
                                        Delai de livraison : jusqu'a 2 semaines apres reception du paiement.
                                    </p>
                                    <InfoBox
                                        icon={<MdOutlineRocketLaunch size={18} />}
                                        title="Mission en cours"
                                        description={`Vous avez deja recu ${offer?.leadsGenerated} leads, soit ${(offer?.leadsGenerated * offer?.buyerPrice?.toFixed(2))?.toFixed(2)}€.`}
                                        palette="green"
                                    />
                                    <div className="rounded-xl bg-leadrs-green-soft p-4">
                                        <div className="mb-2 flex items-center gap-2">
                                            <MdInbox size={18} className="text-leadrs-green-focus" />
                                            <p className="font-geist text-sm font-semibold text-leadrs-green-focus">Accedez a vos leads</p>
                                        </div>
                                        <p className="mb-3 font-geist text-sm text-leadrs-green-focus opacity-80">
                                            Tous vos leads sont centralises dans votre boite de reception.
                                        </p>
                                        <button
                                            className="rounded-lg bg-leadrs-green-focus px-4 py-2 font-geist text-sm font-medium text-white transition-opacity hover:opacity-80"
                                            onClick={() => { window.location.href = '/leads'; }}
                                        >
                                            Voir mes leads
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>

                        <hr className="my-5 border-leadrs-stroke" />

                        {/* Order details */}
                        <p className="mb-4 font-geist text-sm font-semibold text-leadrs-text">Details de la commande</p>
                        <div className="space-y-4">
                            {[
                                { label: 'Quantite', value: offer?.weeklyQuantity ? `${offer?.weeklyQuantity} leads` : null },
                                { label: 'Type de besoin', value: offer?.type || null },
                            ].filter(r => r.value).map(({ label, value }) => (
                                <div key={label} className="flex items-start justify-between gap-4">
                                    <p className="font-geist text-sm font-medium text-leadrs-text whitespace-nowrap">{label}</p>
                                    <p className="font-geist text-sm text-leadrs-text-soft text-right">{value}</p>
                                </div>
                            ))}

                            {offer?.channels?.length > 0 && (
                                <div>
                                    <p className="mb-2 font-geist text-sm font-medium text-leadrs-text">Canaux d'acquisition</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {offer.channels.map((ch: string) => (
                                            <span key={ch} className="rounded border border-leadrs-stroke px-2 py-0.5 font-geist text-xs text-leadrs-text">
                                                {ch}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {offer?.fields?.length > 0 && (
                                <div>
                                    <p className="mb-2 font-geist text-sm font-medium text-leadrs-text">Champs de donnees</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {offer.fields.map((f: string) => (
                                            <span key={f} className="rounded border border-leadrs-stroke px-2 py-0.5 font-geist text-xs text-leadrs-text">
                                                {f}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {offer?.regions?.length > 0 && (
                                <div>
                                    <p className="mb-2 font-geist text-sm font-medium text-leadrs-text">Departements</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {offer.regions.map((r: string) => (
                                            <span key={r} className="rounded border border-leadrs-stroke px-2 py-0.5 font-geist text-xs text-leadrs-text">
                                                {r}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BuyerOfferPage;
